<script>
    import { onMount } from "svelte";
    import {
        salesGridData,
        appliedSalesSearch,
        salesColumnConfig,
        MASTER_SALES_COLUMNS,
        metricRowDefs,
        isSalesLoading,
        salesSort,
        DEFAULT_SORT,
    } from "./store.js";

    // 표시 대상 고정 컬럼들 (컬럼설정에서 지정한 순서)
    $: visibleCols = MASTER_SALES_COLUMNS.filter(c =>
        $salesColumnConfig.visibleColumnIds.includes(c.id)
    ).sort((a, b) =>
        $salesColumnConfig.visibleColumnIds.indexOf(a.id) - $salesColumnConfig.visibleColumnIds.indexOf(b.id)
    );

    // 표시 대상 항목(행) 정의들
    $: metricDefs = $metricRowDefs.filter(m =>
        $salesColumnConfig.visibleMetricIds.includes(m.id)
    );

    $: periodCols = $salesGridData.periodCols || [];
    // 년말확장 컬럼 (계 오른쪽에 차년 +3개월 표시)
    $: extCols = $salesGridData.extCols || [];
    $: gridItems = $salesGridData.items || [];

    // 구간 셀 너비: 금액은 자릿수가 커서 넓게, 대수는 좁게
    $: isAmount = $appliedSalesSearch.unitType === "AMOUNT";
    $: periodW = isAmount ? 120 : 84;
    $: totalW = isAmount ? 135 : 100;

    const PLANWEEK_W = 85;
    const METRIC_W = 80;

    // 컬럼 너비 상태 (기본값: 최소 80px 이상, 글자가 잘리지 않도록 여유 있게 설정)
    let colWidths = {
        productType: 95,
        itemGroup: 130,
        integratedModel: 85,
        model: 85,
        itemCode: 110,
        itemName: 175,
    };

    // 좌측 고정 컬럼 left 오프셋 계산
    function computeLefts(cols, widths) {
        const map = {};
        let left = 0;
        for (const c of cols) {
            map[c.id] = left;
            left += (widths[c.id] || 80);
        }
        return { map, end: left };
    }
    $: lefts = computeLefts(visibleCols, colWidths);

    // 텍스트 시각적 너비 추정 (한글 13px, 영문 대문자 9px, 영숫자/기호 7.5px)
    function estimateTextWidth(text) {
        if (!text) return 0;
        let width = 0;
        const str = String(text);
        for (let i = 0; i < str.length; i++) {
            const code = str.charCodeAt(i);
            if (code > 127) {
                width += 13;
            } else if (code >= 65 && code <= 90) {
                width += 9;
            } else {
                width += 7.5;
            }
        }
        return width;
    }

    // 해당 컬럼의 실제 데이터 중 가장 긴 텍스트 기준 최적 너비 계산
    function calculateMaxContentWidth(colKey, items) {
        const def = MASTER_SALES_COLUMNS.find(c => c.id === colKey);
        let maxVisual = def ? estimateTextWidth(def.label) : 40;

        if (Array.isArray(items) && items.length > 0) {
            // 상위 100개 항목을 순회하며 가장 긴 텍스트 탐색
            const limit = Math.min(items.length, 100);
            for (let i = 0; i < limit; i++) {
                const val = items[i]?.[colKey];
                if (val) {
                    const w = estimateTextWidth(val);
                    if (w > maxVisual) maxVisual = w;
                }
            }
        }
        // 셀 내부 padding (좌우 14px) + 테두리 + 여유공간 (18px)
        const computed = Math.ceil(maxVisual + 32);
        // 최소 80px 보장, 최대 500px 제한
        return Math.max(80, Math.min(computed, 500));
    }

    // 마우스 드래그를 통한 컬럼 너비 조절 (Column Resizing)
    let activeResizeCol = null;
    let resizeStartX = 0;
    let resizeStartWidth = 0;
    let resizeMaxWidth = 500;

    function handleResizeStart(colKey, e) {
        activeResizeCol = colKey;
        resizeStartX = e.clientX;
        resizeStartWidth = colWidths[colKey] || 80;

        // 최대 허용 폭: 데이터 내 가장 긴 글자 너비 (단 최소 80px 이상)
        const maxContentW = calculateMaxContentWidth(colKey, gridItems);
        resizeMaxWidth = Math.max(100, maxContentW);

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        window.addEventListener("mousemove", handleResizeMouseMove);
        window.addEventListener("mouseup", handleResizeMouseUp);
    }

    function handleResizeMouseMove(e) {
        if (!activeResizeCol) return;
        const delta = e.clientX - resizeStartX;
        // 최소 80px, 최대: 가장 긴 글자 너비(resizeMaxWidth)
        const newWidth = Math.max(80, Math.min(resizeMaxWidth, resizeStartWidth + delta));
        colWidths = {
            ...colWidths,
            [activeResizeCol]: newWidth,
        };
    }

    function handleResizeMouseUp() {
        lastResizeEnd = Date.now();
        activeResizeCol = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", handleResizeMouseMove);
        window.removeEventListener("mouseup", handleResizeMouseUp);
    }

    // 컬럼 너비 조절 직후 발생하는 click이 정렬로 이어지지 않도록 종료 시각을 기억
    let lastResizeEnd = 0;

    // 활성 정렬 컬럼 (선택된 컬럼이 숨겨져 있으면 기본 정렬 컬럼으로 표시)
    $: activeSortKey = visibleCols.some(c => c.id === $salesSort.key)
        ? $salesSort.key
        : DEFAULT_SORT.key;
    $: activeSortDir = activeSortKey === $salesSort.key ? $salesSort.dir : DEFAULT_SORT.dir;

    // 헤더 클릭: 다른 컬럼이면 오름차순부터, 같은 컬럼이면 오름 ↔ 내림 전환
    function handleSortClick(colId) {
        if (Date.now() - lastResizeEnd < 300) return;
        salesSort.set({
            key: colId,
            dir: activeSortKey === colId && activeSortDir === "asc" ? "desc" : "asc",
        });
    }

    // 더블클릭 시 가장 긴 글자 너비로 자동 맞춤 (Auto-Fit)
    function handleAutoFit(colKey) {
        const autoWidth = calculateMaxContentWidth(colKey, gridItems);
        colWidths = {
            ...colWidths,
            [colKey]: autoWidth,
        };
    }

    // 금액/수량 및 전년비·경영비(증감 객체) 포맷터
    // 숫자 표시: 소수 둘째 자리까지 (정수면 소수점 없이), 부동소수 오차(-0 등) 제거
    function fmtNum(n, digits = 2) {
        const f = Math.pow(10, digits);
        const r = Math.round((Number(n) || 0) * f) / f;
        return (r === 0 ? 0 : r).toLocaleString(undefined, { maximumFractionDigits: digits });
    }

    function formatValue(val, isRatio) {
        if (val === undefined || val === null) return "-";

        if (isRatio) {
            const diff = val.diff || 0;
            const sign = diff > 0 ? "+" : "";
            // 비율: 정수면 0%, 100% 처럼 표시하고 소수가 있을 때만 소수 첫째 자리까지 (예: 33.3%)
            const ratioTxt = fmtNum(val.ratio, 1);
            if (isAmount) {
                const absDiff = Math.abs(diff);
                const diffTxt = absDiff >= 100000000
                    ? `${fmtNum(diff / 100000000, 1)}억`
                    : absDiff >= 1000000
                        ? `${fmtNum(diff / 1000000, 0)}백만`
                        : fmtNum(diff);
                return `${sign}${ratioTxt}% (${diffTxt})`;
            }
            return `${sign}${ratioTxt}% (${sign}${fmtNum(diff)}대)`;
        }

        return fmtNum(val);
    }

    function ratioClass(val, isRatio) {
        if (!isRatio || !val || typeof val !== "object") return "";
        const diff = val.diff || 0;
        if (diff > 0) return "diff-pos";
        if (diff < 0) return "diff-neg";
        return "";
    }

    // 무한 스크롤(점진적 렌더링) 설정
    const INITIAL_COUNT = 30;
    const BATCH_SIZE = 30;
    let displayCount = INITIAL_COUNT;
    let scrollWrapper;

    let prevItemsRef = null;
    $: if ($salesGridData.items && $salesGridData.items !== prevItemsRef) {
        prevItemsRef = $salesGridData.items;
        displayCount = INITIAL_COUNT;
        if (scrollWrapper) {
            scrollWrapper.scrollTop = 0;
        }
    }

    $: displayedItems = gridItems.slice(0, displayCount);
    $: hasMore = gridItems.length > displayCount;

    let isScrolling = false;
    function handleScroll() {
        if (!hasMore || isScrolling) return;
        isScrolling = true;
        window.requestAnimationFrame(() => {
            if (scrollWrapper) {
                const scrollBottom = scrollWrapper.scrollHeight - scrollWrapper.scrollTop - scrollWrapper.clientHeight;
                if (scrollBottom < 600) {
                    displayCount = Math.min(displayCount + BATCH_SIZE, gridItems.length);
                }
            }
            isScrolling = false;
        });
    }

    function loadAll() {
        displayCount = gridItems.length;
    }

    // 전체 합계 상단 표시 토글 (백그라운드 사전 렌더링 + 0ms 즉각 반응 스르륵 슬라이드)
    let isSummaryOpen = false;
    function toggleTopSummary() {
        isSummaryOpen = !isSummaryOpen;
    }

    // Ctrl 키 누른 상태에서 마우스 드래그로 화면 이동 (Pan / Grab Tool)
    let isCtrlDown = false;
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;

    function handleKeyDown(e) {
        if (e.key === "Control" || e.ctrlKey || e.key === "Meta" || e.metaKey) {
            isCtrlDown = true;
        }
    }

    function handleKeyUp(e) {
        if (e.key === "Control" || e.key === "Meta" || (!e.ctrlKey && !e.metaKey)) {
            isCtrlDown = false;
            if (isPanning) {
                isPanning = false;
            }
        }
    }

    function handleWindowBlur() {
        isCtrlDown = false;
        isPanning = false;
    }

    function handleMouseDown(e) {
        if ((e.ctrlKey || e.metaKey || isCtrlDown) && e.button === 0) {
            if (!scrollWrapper) return;
            isPanning = true;
            startX = e.clientX;
            startY = e.clientY;
            startScrollLeft = scrollWrapper.scrollLeft;
            startScrollTop = scrollWrapper.scrollTop;
            e.preventDefault();
        }
    }

    function handleWindowMouseMove(e) {
        if (!isPanning) {
            isCtrlDown = !!(e.ctrlKey || e.metaKey);
            return;
        }
        if (!scrollWrapper) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        scrollWrapper.scrollLeft = startScrollLeft - dx;
        scrollWrapper.scrollTop = startScrollTop - dy;
    }

    function handleWindowMouseUp() {
        if (isPanning) {
            isPanning = false;
        }
    }

    onMount(() => {
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);
        window.addEventListener("blur", handleWindowBlur);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("mouseup", handleWindowMouseUp);
            window.removeEventListener("blur", handleWindowBlur);
        };
    });
</script>

<div class="list-container">
    <!-- 로딩 오버레이 (조회 중 전체 페이지 표시) -->
    {#if $isSalesLoading}
        <div class="loading-overlay">
            <div class="loading-modal">
                <div class="spinner"></div>
                <div class="loading-text">
                    <strong>매출현황 데이터 조회 및 계산 중...</strong>
                    <span>실적, 경영, 계획 데이터를 집계하고 있습니다.</span>
                </div>
            </div>
        </div>
    {/if}

    <div class="list-header">
        <div class="title-area">
            <div class="title-left">
                <h3>
                    📊 매출현황 집계표
                    <span class="badge-count">총 {gridItems.length}개 품번</span>
                </h3>
            </div>
            <div class="header-right-group">
                <div class="banner-info-tags">
                    <span class="info-tag">기준: <strong>1년 고정</strong></span>
                    <span class="info-tag">실적: <strong>{$appliedSalesSearch.perfType === 'SALES' ? '판매' : '출하'}</strong></span>
                    <span class="info-tag">단위: <strong>{isAmount ? '금액 기준' : '대수 기준'}</strong></span>
                    <span class="info-tag">구간: <strong>{$appliedSalesSearch.periodType === 'MONTH' ? '월 단위' : ($appliedSalesSearch.periodType === 'WEEK' ? '주 단위' : '분기 단위')}</strong></span>
                </div>
                <div class="header-action-group">
                    <button
                        type="button"
                        class="action-btn toggle-summary-btn"
                        class:is-active={isSummaryOpen}
                        on:click={toggleTopSummary}
                        title={isSummaryOpen ? '상단 전체 합계 행 접기' : '상단 전체 합계 행 펼쳐보기'}
                    >
                        {#if isSummaryOpen}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
                                <polyline points="18 15 12 9 6 15"/>
                            </svg>
                            <span>📊 전체합계 숨기기</span>
                        {:else}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                            <span>📊 전체합계 상단 보기</span>
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div
        class="table-scroll-wrapper"
        class:pan-ready={isCtrlDown && !isPanning}
        class:is-panning={isPanning}
        bind:this={scrollWrapper}
        on:scroll={handleScroll}
        on:mousedown={handleMouseDown}
        on:dragstart|preventDefault
    >
        <table class="psi-table" style="--period-w: {periodW}px; --total-w: {totalW}px;">
            <thead>
                <tr class="psi-header-row">
                    {#each visibleCols as col}
                        <th
                            class="sticky-col col-custom sortable-th"
                            class:sort-active={activeSortKey === col.id}
                            style="left: {lefts.map[col.id]}px; width: {colWidths[col.id] || 80}px; min-width: {colWidths[col.id] || 80}px; max-width: {colWidths[col.id] || 80}px;"
                            aria-sort={activeSortKey === col.id ? (activeSortDir === "asc" ? "ascending" : "descending") : "none"}
                            on:click={() => handleSortClick(col.id)}
                            title="클릭: {col.label} 기준 정렬 (오름차순 ↔ 내림차순)"
                        >
                            <span class="col-header-text">
                                {col.label}
                                <span class="sort-icon" aria-hidden="true">{activeSortKey === col.id ? (activeSortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
                            </span>
                            <div
                                class="col-resize-handle"
                                class:is-active={activeResizeCol === col.id}
                                on:mousedown|stopPropagation={(e) => handleResizeStart(col.id, e)}
                                on:click|stopPropagation
                                on:dblclick|stopPropagation={() => handleAutoFit(col.id)}
                                title="드래그: 너비 조절 (최소 80px) / 더블클릭: 가장 긴 글자에 맞춤"
                            ></div>
                        </th>
                    {/each}
                    <th
                        class="sticky-col col-planweek"
                        style="left: {lefts.end}px; width: {PLANWEEK_W}px; min-width: {PLANWEEK_W}px;"
                    >
                        편성주차
                    </th>
                    <th
                        class="sticky-col col-metric"
                        style="left: {lefts.end + PLANWEEK_W}px; width: {METRIC_W}px; min-width: {METRIC_W}px;"
                    >
                        항목
                    </th>

                    {#each periodCols as pCol}
                        <th class="week-th"><span>{pCol.label}</span></th>
                    {/each}
                    <th class="month-subtotal-th">계</th>
                    {#each extCols as eCol}
                        <th class="week-th ext-th" class:ext-first={eCol.isExtFirst} class:ext-last={eCol.isExtLast}>
                            <span>{eCol.label}</span>
                        </th>
                    {/each}
                </tr>

                <!-- [상단 전체합계 행] 백그라운드에 사전 렌더링하여 클릭 시 0ms 렉 없이 즉각 스르륵 개폐 -->
                {#if gridItems.length > 0 && $salesGridData.grandTotal}
                    {@const grand = $salesGridData.grandTotal}
                    {#each metricDefs as metric, mIdx}
                        {@const totVal = grand[metric.id]?.total}
                        <tr
                            class="grand-summary-row summary-top-mode"
                            class:summary-first={mIdx === 0}
                            class:product-last-row={mIdx === metricDefs.length - 1}
                            class:diff-row={metric.isRatio}
                            class:summary-collapsed={!isSummaryOpen}
                        >
                            {#if mIdx === 0}
                                <td
                                    colspan={visibleCols.length}
                                    rowspan={metricDefs.length}
                                    class="sticky-col col-total-label bold"
                                    style="left: 0;"
                                >
                                    <div class="summary-cell-wrap summary-total-badge" class:is-open={isSummaryOpen}>
                                        전체 합계 (Total)
                                    </div>
                                </td>
                                <td
                                    rowspan={metricDefs.length}
                                    class="sticky-col col-planweek planweek-cell font-mono bold"
                                    style="left: {lefts.end}px; width: {PLANWEEK_W}px; min-width: {PLANWEEK_W}px;"
                                >
                                    <div class="summary-cell-wrap summary-planweek-wrap" class:is-open={isSummaryOpen}>
                                        {$appliedSalesSearch.planWeek || "-"}
                                    </div>
                                </td>
                            {/if}

                            <td
                                class="sticky-col col-metric metric-cell bold"
                                class:metric-shortage={metric.id === 'mgmtPlan'}
                                class:diff-metric={metric.isRatio}
                                style="left: {lefts.end + PLANWEEK_W}px; width: {METRIC_W}px; min-width: {METRIC_W}px;"
                            >
                                <div class="summary-cell-wrap" class:is-open={isSummaryOpen}>
                                    {metric.label}
                                </div>
                            </td>

                            {#each periodCols as pCol}
                                {@const val = grand[metric.id]?.[pCol.key]}
                                <td class="cell-num bold {ratioClass(val, metric.isRatio)}" class:diff-cell={metric.isRatio}>
                                    <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                        {formatValue(val, metric.isRatio)}
                                    </div>
                                </td>
                            {/each}
                            <td class="cell-num month-subtotal-cell bold {ratioClass(totVal, metric.isRatio)}">
                                <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                    {formatValue(totVal, metric.isRatio)}
                                </div>
                            </td>
                            {#each extCols as eCol}
                                {@const eVal = grand[metric.id]?.[eCol.key]}
                                <td
                                    class="cell-num bold ext-cell {ratioClass(eVal, metric.isRatio)}"
                                    class:diff-cell={metric.isRatio}
                                    class:ext-first={eCol.isExtFirst}
                                    class:ext-last={eCol.isExtLast}
                                >
                                    <div class="summary-cell-wrap text-right" class:is-open={isSummaryOpen}>
                                        {formatValue(eVal, metric.isRatio)}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                {/if}
            </thead>

            <tbody>
                {#if gridItems.length === 0}
                    <tr>
                        <td colspan="100" class="empty-state">
                            <div class="empty-content">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <circle cx="11" cy="11" r="8"/>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                                <p>조회 조건에 맞는 매출현황 데이터가 없습니다.</p>
                            </div>
                        </td>
                    </tr>
                {:else}
                    {#each displayedItems as item}
                        {#each metricDefs as metric, mIdx}
                            {@const rowMap = item.rowDataMap[metric.id]}
                            {@const totVal = rowMap?.total}
                            <tr
                                class="data-row base-row"
                                class:product-first-row={mIdx === 0}
                                class:product-last-row={mIdx === metricDefs.length - 1}
                                class:diff-row={metric.isRatio}
                            >
                                <!-- 제품 마스터 컬럼들 (첫 번째 행에서 rowspan 렌더링) -->
                                {#if mIdx === 0}
                                    {#each visibleCols as col}
                                        <td
                                            rowspan={metricDefs.length}
                                            class="sticky-col col-custom master-cell"
                                            class:bold={col.id === 'itemGroup' || col.id === 'productType'}
                                            class:font-mono={col.id === 'itemCode'}
                                            style="left: {lefts.map[col.id]}px; width: {colWidths[col.id] || 80}px; min-width: {colWidths[col.id] || 80}px; max-width: {colWidths[col.id] || 80}px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;"
                                            title="{item[col.id] || ''}"
                                        >
                                            {item[col.id] || "-"}
                                        </td>
                                    {/each}

                                    <td
                                        rowspan={metricDefs.length}
                                        class="sticky-col col-planweek planweek-cell font-mono"
                                        style="left: {lefts.end}px; width: {PLANWEEK_W}px; min-width: {PLANWEEK_W}px;"
                                    >
                                        {item.planWeek || $appliedSalesSearch.planWeek || "-"}
                                    </td>
                                {/if}

                                <!-- 항목명 -->
                                <td
                                    class="sticky-col col-metric metric-cell"
                                    class:metric-shortage={metric.id === 'mgmtPlan'}
                                    class:diff-metric={metric.isRatio}
                                    style="left: {lefts.end + PLANWEEK_W}px; width: {METRIC_W}px; min-width: {METRIC_W}px;"
                                    title="{metric.label}"
                                >
                                    {metric.label}
                                </td>

                                <!-- 구간별 데이터 -->
                                {#each periodCols as pCol}
                                    {@const val = rowMap?.[pCol.key]}
                                    <td class="cell-num {ratioClass(val, metric.isRatio)}" class:diff-cell={metric.isRatio}>
                                        {formatValue(val, metric.isRatio)}
                                    </td>
                                {/each}

                                <!-- 계 셀 -->
                                <td class="cell-num month-subtotal-cell {ratioClass(totVal, metric.isRatio)}">
                                    {formatValue(totVal, metric.isRatio)}
                                </td>

                                <!-- 년말확장 (차년 +3개월) -->
                                {#each extCols as eCol}
                                    {@const eVal = rowMap?.[eCol.key]}
                                    <td
                                        class="cell-num ext-cell {ratioClass(eVal, metric.isRatio)}"
                                        class:diff-cell={metric.isRatio}
                                        class:ext-first={eCol.isExtFirst}
                                        class:ext-last={eCol.isExtLast}
                                    >
                                        {formatValue(eVal, metric.isRatio)}
                                    </td>
                                {/each}
                            </tr>
                        {/each}
                    {/each}

                    <!-- 무한 스크롤 추가 로딩 안내 및 전체 펼치기 -->
                    {#if hasMore}
                        <tr class="infinite-loading-row">
                            <td colspan="100" class="infinite-loading-cell">
                                <div class="infinite-loading-content">
                                    <span class="mini-spinner"></span>
                                    <span class="infinite-text">스크롤을 아래로 내리면 추가 품목이 자동으로 로딩됩니다. ({displayedItems.length} / {gridItems.length}개 표시 중)</span>
                                    <button type="button" class="btn-load-all" on:click={loadAll}>⚡ 전체 한 번에 펼치기 ({gridItems.length}개)</button>
                                </div>
                            </td>
                        </tr>
                    {/if}
                {/if}
            </tbody>
        </table>
    </div>
</div>

<style>
    .list-container {
        background: #ffffff;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex: 1;
        min-height: 0;
        height: 100%;
    }

    .list-header {
        padding: 10px 16px;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
    }
    .title-area {
        display: flex;
        align-items: center;
        width: 100%;
        justify-content: space-between;
    }
    .title-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .title-area h3 {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 800;
        color: #1e293b;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .badge-count {
        font-size: 0.72rem;
        background: #e2e8f0;
        color: #475569;
        padding: 2px 8px;
        border-radius: 12px;
        font-weight: 700;
    }

    .header-right-group {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    .header-action-group {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        font-size: 0.74rem;
        font-weight: 700;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;
        line-height: 1.2;
    }
    .toggle-summary-btn {
        background: #f8fafc;
        border: 1px solid #cbd5e1;
        color: #334155;
    }
    .toggle-summary-btn:hover {
        background: #f1f5f9;
        border-color: #94a3b8;
        color: #0f172a;
    }
    .toggle-summary-btn.is-active {
        background: #eff6ff;
        border-color: #93c5fd;
        color: #1d4ed8;
    }

    /* 테이블 스크롤 영역 */
    .table-scroll-wrapper {
        flex: 1;
        min-height: 0;
        overflow: auto;
        position: relative;
    }
    .table-scroll-wrapper::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    .table-scroll-wrapper::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
    }
    .table-scroll-wrapper::-webkit-scrollbar-track {
        background: #f8fafc;
    }

    /* Ctrl 키 활성화 시 잡기(손바닥) 커서 및 패닝 스크롤 스타일 */
    .table-scroll-wrapper.pan-ready,
    .table-scroll-wrapper.pan-ready * {
        cursor: grab !important;
        user-select: none !important;
    }

    .table-scroll-wrapper.is-panning,
    .table-scroll-wrapper.is-panning * {
        cursor: grabbing !important;
        user-select: none !important;
    }

    .psi-table {
        table-layout: fixed;
        width: max-content;
        min-width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 0.82rem;
        text-align: center;
        color: #1e293b;
    }

    /* 헤더 스타일 (단일 행 헤더 + 상단 전체합계 sticky 고정) */
    .psi-table thead {
        position: sticky;
        top: 0;
        z-index: 25;
        background: #f1f5f9;
    }
    .psi-table th {
        background: #f1f5f9;
        color: #334155;
        font-weight: 700;
        padding: 5px 6px;
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #cbd5e1;
        border-top: 1px solid #cbd5e1;
        white-space: nowrap;
        font-size: 0.78rem;
        background-clip: padding-box;
        height: 34px;
        box-sizing: border-box;
    }
    .psi-table th:first-child {
        border-left: 1px solid #cbd5e1;
    }

    .psi-header-row {
        height: 34px;
    }
    .psi-table thead .sticky-col {
        z-index: 45;
        background: #f1f5f9;
    }

    .week-th {
        width: 44px;
        min-width: 44px;
        max-width: 44px;
        padding: 4px 1px !important;
        background: #ffffff !important;
        font-variant-numeric: tabular-nums;
        font-size: 0.73rem;
    }
    .week-th span {
        letter-spacing: -0.3px;
        display: inline-block;
    }
    .actual-th {
        background: #f8fafc !important;
    }
    .month-subtotal-th {
        background: #fef3c7 !important;
        color: #92400e;
        font-weight: 800;
        width: 70px;
        min-width: 70px;
        max-width: 70px;
        padding: 4px 3px !important;
        font-size: 0.74rem;
        letter-spacing: -0.3px;
        border-left: 2px solid #fde68a !important;
        border-right: 2px solid #fde68a !important;
        white-space: nowrap;
    }
    .total-th {
        background: #e0e7ff !important;
        color: #3730a3;
        font-weight: 800;
        width: 68px;
        min-width: 68px;
        max-width: 68px;
        padding: 4px 3px !important;
        font-size: 0.75rem;
        letter-spacing: -0.3px;
        border-left: 2px solid #c7d2fe !important;
        white-space: nowrap;
    }

    /* Sticky 좌측 고정 컬럼 */
    .sticky-col {
        position: sticky !important;
        background: #ffffff;
        z-index: 10;
        border: 1px solid #cbd5e1;
        background-clip: padding-box;
        box-sizing: border-box;
    }

    th.col-custom {
        position: sticky !important;
        user-select: none;
        overflow: visible;
        padding-left: 6px !important;
        padding-right: 6px !important;
    }

    td.col-custom {
        position: sticky !important;
        user-select: text;
        padding-left: 6px !important;
        padding-right: 6px !important;
    }

    .col-header-text {
        display: block;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
    }
    /* 정렬 가능한 헤더: 클릭 가능 표시 + 활성 정렬 컬럼 강조 */
    .sortable-th {
        cursor: pointer;
    }
    .sortable-th:hover {
        background: #e2e8f0 !important;
    }
    .sort-icon {
        margin-left: 3px;
        font-size: 0.62rem;
        color: #94a3b8;
    }
    .sortable-th:hover .sort-icon {
        color: #475569;
    }
    .sortable-th.sort-active .sort-icon {
        color: #2563eb;
    }
    .col-resize-handle {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 6px;
        cursor: col-resize;
        z-index: 55;
        background: transparent;
        transition: background 0.15s ease;
    }
    .col-resize-handle:hover,
    .col-resize-handle.is-active {
        background: #3b82f6 !important;
    }

    .col-metric {
        box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
    }

    /* 데이터 셀 스타일 */
    .psi-table td {
        padding: 4px 6px;
        border: 1px solid #e2e8f0;
        white-space: nowrap;
        height: 25px;
        box-sizing: border-box;
    }
    .cell-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        padding: 3px 4px !important;
        color: #1e293b;
        font-size: 0.76rem;
        width: 44px;
        min-width: 44px;
        max-width: 44px;
        box-sizing: border-box;
    }
    .month-subtotal-cell {
        width: 70px;
        min-width: 70px;
        max-width: 70px;
        padding: 3px 4px !important;
        box-sizing: border-box;
    }
    .grand-total-cell {
        width: 68px;
        min-width: 68px;
        max-width: 68px;
        padding: 3px 4px !important;
        box-sizing: border-box;
    }

    .master-cell {
        background: #f8fafc;
        vertical-align: middle;
        font-size: 0.78rem;
    }
    .planweek-cell {
        vertical-align: middle;
        background: #f8fafc;
        font-size: 0.78rem;
        color: #334155;
    }
    .compare-week-tag {
        background: #fdf4ff;
        color: #86198f;
    }
    .diff-tag {
        background: #f0fdf4;
        color: #166534;
    }
    .metric-cell {
        background: #ffffff;
        font-weight: 700;
        color: #475569;
        font-size: 0.78rem;
        cursor: help;
    }
    .metric-shortage {
        color: #dc2626 !important;
        font-weight: 800;
    }

    .actual-cell {
        background: #f8fafc;
    }

    .month-subtotal-cell {
        background: #fffbeb;
        font-weight: 700;
        color: #92400e;
        border-left: 2px solid #fde68a;
        border-right: 2px solid #fde68a;
    }
    .grand-total-cell {
        background: #eef2ff;
        font-weight: 800;
        color: #3730a3;
        border-left: 2px solid #c7d2fe;
    }

    .diff-row {
        background: #f0fdf4;
    }
    .diff-row .sticky-col {
        background: #f0fdf4;
    }
    .diff-metric {
        color: #166534;
    }
    .diff-pos {
        color: #16a34a;
        font-weight: 700;
    }
    .diff-neg {
        color: #1e293b;
        font-weight: 600;
    }

    .product-first-row td {
        border-top: 2px solid #94a3b8;
    }
    .product-last-row td {
        border-bottom: 2px solid #94a3b8;
    }

    /* 상단 전체 합계 행 애니메이션 (스르륵 펼쳐지고 접히는 매끄러운 트랜지션) */
    .grand-summary-row {
        background: #f8fafc;
    }
    .grand-summary-row td {
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #cbd5e1;
        background: #f8fafc;
        padding: 0 4px !important;
        height: auto !important;
        overflow: hidden;
        white-space: nowrap;
        box-sizing: border-box;
        transition: border-color 0.28s ease;
    }
    .grand-summary-row td:first-child {
        border-left: 1px solid #cbd5e1;
    }

    .summary-cell-wrap {
        height: 25px;
        line-height: 25px;
        opacity: 1;
        overflow: hidden;
        white-space: nowrap;
        box-sizing: border-box;
        transition: height 0.22s cubic-bezier(0.2, 0.9, 0.3, 1),
                    opacity 0.18s ease-out;
    }
    .summary-cell-wrap.text-right {
        text-align: right;
    }
    .summary-total-badge {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .summary-planweek-wrap {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* 닫힐 때: 모든 내부 래퍼 높이를 0으로 수렴 */
    .summary-cell-wrap:not(.is-open) {
        height: 0px !important;
        opacity: 0 !important;
        pointer-events: none;
    }

    /* 닫혔을 때 테두리 및 여백 완전 제거 (0px 완벽 밀착) */
    .grand-summary-row.summary-collapsed td {
        border-color: transparent !important;
        border-width: 0 !important;
        border: none !important;
        height: 0px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        box-shadow: none !important;
    }

    .summary-top-mode {
        background: #f8fafc;
    }
    .summary-top-mode:not(.summary-collapsed):last-of-type td,
    .summary-top-mode:not(.summary-collapsed).product-last-row td {
        border-bottom: 2px solid #64748b !important;
        box-shadow: 0 4px 6px -2px rgba(0, 0, 0, 0.1);
    }
    .grand-summary-row .sticky-col {
        z-index: 40;
    }
    .col-total-label {
        background: #e2e8f0;
        vertical-align: middle;
        font-size: 0.85rem;
        color: #0f172a;
    }

    .bold { font-weight: 700; }
    .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
    .text-primary { color: #2563eb; }

    .empty-state {
        padding: 40px;
        text-align: center;
        background: #ffffff;
    }
    .empty-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #94a3b8;
    }
    .empty-content p {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 600;
    }

    /* 로딩 인디케이터 오버레이 (조회 중 전체 페이지 표시) */
    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.88);
        backdrop-filter: blur(3px);
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .loading-modal {
        background: #ffffff;
        border: 1.5px solid #cbd5e1;
        border-radius: 8px;
        padding: 24px 32px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        max-width: 420px;
        text-align: center;
    }
    .spinner {
        width: 36px;
        height: 36px;
        border: 3.5px solid #e2e8f0;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    .loading-text strong {
        display: block;
        font-size: 0.95rem;
        color: #1e293b;
        margin-bottom: 6px;
    }
    .loading-text span {
        font-size: 0.8rem;
        color: #64748b;
        line-height: 1.4;
    }

    /* 무한 스크롤 안내 및 펼치기 버튼 */
    .infinite-loading-row {
        background: #f8fafc;
    }
    .infinite-loading-cell {
        padding: 16px 20px;
        text-align: center;
        border-bottom: 2px dashed #cbd5e1;
        background: #f8fafc;
    }
    .infinite-loading-content {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: #ffffff;
        padding: 8px 18px;
        border-radius: 20px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }
    .mini-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid #cbd5e1;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        display: inline-block;
    }
    .infinite-text {
        font-size: 0.8rem;
        color: #475569;
        font-weight: 600;
    }
    .btn-load-all {
        padding: 4px 10px;
        font-size: 0.75rem;
        font-weight: 700;
        color: #2563eb;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .btn-load-all:hover {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1d4ed8;
    }


    /* ===== 매출현황 전용 보정 (공급계획 표 스타일 위에 덮어씀) ===== */
    .title-left {
        flex-wrap: wrap;
    }
    .banner-info-tags {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .info-tag {
        font-size: 0.72rem;
        color: #64748b;
        background: #ffffff;
        padding: 2px 7px;
        border-radius: 4px;
        border: 1px solid #e2e8f0;
        white-space: nowrap;
    }
    .info-tag strong {
        color: #1e293b;
    }

    /* 구간(월/주/분기) 셀 너비는 집계단위에 따라 가변 */
    .week-th {
        width: var(--period-w);
        min-width: var(--period-w);
        max-width: var(--period-w);
        padding: 4px 4px !important;
        font-size: 0.78rem;
    }
    .cell-num {
        width: var(--period-w);
        min-width: var(--period-w);
        max-width: var(--period-w);
    }
    /* 계 컬럼 (와이어프레임의 노란색 계) */
    .month-subtotal-th,
    .month-subtotal-cell {
        width: var(--total-w);
        min-width: var(--total-w);
        max-width: var(--total-w);
    }
    .diff-neg {
        color: #dc2626;
        font-weight: 700;
    }
    .metric-cell {
        cursor: default;
    }

    /* 년말확장 컬럼: 계 오른쪽에 파란 테두리로 구분 (와이어프레임) */
    .ext-th {
        background: #eff6ff !important;
        color: #1d4ed8;
        font-weight: 800;
        border-top: 2px solid #2563eb !important;
    }
    .ext-cell {
        background: #f8fbff;
    }
    .ext-first {
        border-left: 2px solid #2563eb !important;
    }
    .ext-last {
        border-right: 2px solid #2563eb !important;
    }
</style>
